/**
 * Shared readiness field-label map for the strategy surfaces.
 *
 * The strategy edge functions reject a not-ready client with a
 * `validation_error` whose `fields` map is keyed by readiness fields. Both
 * `StrategyPage` (template create) and `useStrategyGenerationJob` (AI generate)
 * translate those keys to Arabic labels — kept here so the two surfaces stay in
 * sync.
 *
 * Task 274 mapped the generic keys emitted by the `enforceReadiness` in repo
 * master (`brand_info` / `onboarding_profile` / `target_audiences` /
 * `client_goals`).
 *
 * Task 275 — the DEPLOYED `generate-client-strategy-ai` (Task 092C readiness
 * gate, edge v93) emits a richer, DOTTED key set (`brand_info.industry`,
 * `onboarding.mission`, …) that the master keys do not cover, so its
 * validation errors fell through to the generic "تعذّر تشغيل التوليد" message
 * instead of naming the missing field. Both key schemas are mapped below so the
 * UI names the field regardless of which gate version is live. (The master ≠
 * prod source drift itself is tracked separately —
 * `tasks/276-generate-client-strategy-ai-drift.md`.)
 */

const STRATEGY_READINESS_FIELD_LABELS: Readonly<Record<string, string>> = {
  // Master (pre-092C) generic keys.
  brand_info: 'هوية العلامة',
  onboarding_profile: 'ملف التأهيل',
  client_goals: 'الأهداف',
  // Shared by both schemas.
  target_audiences: 'الجمهور المستهدف',
  // Deployed Task 092C dotted keys.
  'brand_info.brand_name': 'اسم العلامة',
  'brand_info.industry': 'القطاع',
  'brand_info.website_url': 'رابط الموقع',
  'onboarding.description': 'وصف العلامة',
  'onboarding.mission': 'الرسالة',
  'onboarding.vision': 'الرؤية',
  'onboarding.values_text': 'القيم',
  'onboarding.usp': 'القيمة المميزة',
  'onboarding.products_services': 'المنتجات/الخدمات',
  'onboarding.competitors': 'المنافسون',
};

/** Every readiness field key the UI knows how to label (both schemas). */
export const STRATEGY_READINESS_FIELD_KEYS: ReadonlyArray<string> = Object.keys(
  STRATEGY_READINESS_FIELD_LABELS,
);

/** True when `key` is a recognised strategy readiness field key. */
export function isStrategyReadinessFieldKey(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(STRATEGY_READINESS_FIELD_LABELS, key);
}

/** Arabic label for a readiness field key; echoes the raw key for unknowns. */
export function readinessFieldLabel(key: string): string {
  return STRATEGY_READINESS_FIELD_LABELS[key] ?? key;
}
